// Import React helpers.
import React, { useEffect, useState } from "react";



// Export the reusable hero carousel.
export const HeroCarousel = ({
    slides = [],
    createAccountUrl,
    interval = 7000,
}) => {

    // Store the index of the currently visible slide.
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);

    // Store whether automatic slide movement should temporarily pause.
    const [carouselPaused, setCarouselPaused] = useState(false);



    // Get the total number of available slides.
    const slideCount = slides.length;



    // Move to the previous carousel slide.
    const handlePreviousSlide = () => {

        // Update the selected slide index.
        setActiveSlideIndex((currentIndex) => {

            // Move from the first slide back to the final slide.
            if (currentIndex === 0) {

                // Return the final available slide index.
                return slideCount - 1;

            }

            // Otherwise move backwards by one slide.
            return currentIndex - 1;

        });

    };



    // Move to the next carousel slide.
    const handleNextSlide = () => {

        // Update the selected slide index.
        setActiveSlideIndex((currentIndex) => {

            // Return the next slide while wrapping back to zero.
            return (currentIndex + 1) % slideCount;

        });

    };



    // Allow keyboard navigation through the carousel.
    const handleCarouselKeyDown = (event) => {

        // Move backwards when the left arrow is pressed.
        if (event.key === "ArrowLeft") {

            // Show the previous slide.
            handlePreviousSlide();

        }

        // Move forwards when the right arrow is pressed.
        if (event.key === "ArrowRight") {

            // Show the next slide.
            handleNextSlide();

        }

    };



    // Automatically rotate between available slides.
    useEffect(() => {

        // Do not create a timer when fewer than two slides exist.
        if (slideCount < 2) {

            // Exit the effect.
            return undefined;

        }

        // Do not rotate while the user is interacting with the carousel.
        if (carouselPaused) {

            // Exit the effect.
            return undefined;

        }



        // Create the automatic carousel timer.
        const carouselTimer = window.setInterval(() => {

            // Move to the next slide.
            setActiveSlideIndex((currentIndex) => {

                // Wrap around when the final slide is reached.
                return (currentIndex + 1) % slideCount;

            });

        }, interval);



        // Return the timer cleanup function.
        return () => {

            // Remove the timer before recreating it or unmounting.
            window.clearInterval(carouselTimer);

        };

    // Recreate the timer when these values change.
    }, [
        carouselPaused,
        interval,
        slideCount,
    ]);



    /*
    // Scroll smoothly to the FAQ section.
    const handleSeeMoreDetailsClick = (type) => {

        // Find the FAQ section in the page.
        const goToSection = document.getElementById(type);

        // Stop when the FAQ section cannot be found.
        if (!goToSection) {

            // Exit the handler.
            return;

        }

        // Scroll the FAQ section into view.
        goToSection.scrollIntoView({

            // Animate the scroll.
            behavior: "smooth",

            // Position the FAQ section at the top.
            block: "start",

        });

    };
    */



    // Return nothing when there are no available slides.
    if (slideCount === 0) {

        // Prevent an empty carousel wrapper being rendered.
        return null;

    }



    // Render the carousel.
    return (

        // Render the complete hero section.
        <section
            className="hero-carousel"
            aria-roledescription="carousel"
            aria-label="StrichSuite features"
            onMouseEnter={() => setCarouselPaused(true)}
            onMouseLeave={() => setCarouselPaused(false)}
            onFocus={() => setCarouselPaused(true)}
            onBlur={() => setCarouselPaused(false)}
            onKeyDown={handleCarouselKeyDown}
        >

            {/* Render all carousel slides. */}
            <div className="hero-carousel__slides">

                {/* Loop through the API-provided carousel slides. */}
                {slides.map((slide, slideIndex) => {

                    // Determine whether this is the currently visible slide.
                    const slideIsActive = slideIndex === activeSlideIndex;



                    // Render the individual carousel slide.
                    return (

                        // Render the slide container.
                        <article
                            className={[
                                "hero-carousel__slide",
                                slideIsActive
                                    ? "hero-carousel__slide--active"
                                    : "",
                            ].filter(Boolean).join(" ")}
                            key={slide?.id ?? slideIndex}
                            aria-hidden={!slideIsActive}
                            style={{
                                backgroundColor:
                                    slide?.backgroundColor ?? "#ffffff",
                            }}
                        >

                            {/* Keep each slide aligned to the same maximum width. */}
                            <div className="hero-carousel__slide-inner">

                                {/* Render the text side of the slide. */}
                                <div className="hero-carousel__content">

                                    {/* Render the main slide heading. */}
                                    <h1 className="hero-carousel__title">
                                        {slide?.title}
                                    </h1>

                                    {/* Render the slide supporting text. */}
                                    <p className="hero-carousel__description">
                                        {slide?.description}
                                    </p>

                                    {/* Render both call-to-action buttons. */}
                                    <div className="hero-carousel__actions">

                                        {/* Link to the detailed feature page.
                                        <button
                                            className="hero-carousel__button hero-carousel__button--secondary"
                                            href={slide?.detailsUrl}
                                            tabIndex={slideIsActive ? 0 : -1}
                                            onClick={() => {

                                                if (slide.journeyKey) {
                                                    
                                                    selectJourney(slide.journeyKey);

                                                }

                                                handleSeeMoreDetailsClick(slide.scrollToId);

                                            }}
                                        >
                                            See More Details
                                        </button>
                                        */}

                                        {/* Link to account registration. */}
                                        <a
                                            className="hero-carousel__button hero-carousel__button--primary"
                                            href={createAccountUrl}
                                            tabIndex={slideIsActive ? 0 : -1}
                                        >
                                            Create a Free Account
                                        </a>

                                    </div>

                                </div>

                                {/* Render the visual side of the slide. */}
                                <div className="hero-carousel__visual">

                                    {/* Render exactly one image for every slide. */}
                                    <img
                                        className="hero-carousel__image"
                                        src={slide?.image}
                                        alt={slide?.imageAlt ?? ""}
                                    />

                                </div>

                            </div>

                        </article>

                    );

                })}

            </div>



            {/* Only display carousel navigation when multiple slides exist. */}
            {slideCount > 1 && (

                // Group the carousel navigation controls.
                <>

                    {/* Move to the previous slide. */}
                    <button
                        className="hero-carousel__arrow hero-carousel__arrow--previous"
                        type="button"
                        onClick={handlePreviousSlide}
                        aria-label="Previous slide"
                    >
                        ‹
                    </button>

                    {/* Move to the next slide. */}
                    <button
                        className="hero-carousel__arrow hero-carousel__arrow--next"
                        type="button"
                        onClick={handleNextSlide}
                        aria-label="Next slide"
                    >
                        ›
                    </button>

                    {/* Render the carousel position indicators. */}
                    <div
                        className="hero-carousel__pagination"
                        aria-label="Choose carousel slide"
                    >

                        {/* Render one indicator for every slide. */}
                        {slides.map((slide, slideIndex) => {

                            // Determine whether this pagination item is selected.
                            const paginationIsActive =
                                slideIndex === activeSlideIndex;

                            // Render the pagination button.
                            return (

                                // Allow the user to jump directly to this slide.
                                <button
                                    className={[
                                        "hero-carousel__pagination-button",
                                        paginationIsActive
                                            ? "hero-carousel__pagination-button--active"
                                            : "",
                                    ].filter(Boolean).join(" ")}
                                    key={slide?.id ?? slideIndex}
                                    type="button"
                                    onClick={() => setActiveSlideIndex(slideIndex)}
                                    aria-label={`Show slide ${slideIndex + 1}`}
                                    aria-current={paginationIsActive ? "true" : undefined}
                                />

                            );

                        })}

                    </div>

                </>

            )}

        </section>

    );

};